/**
 * Super Admin Subscription Plan Service
 * Handles subscription plan API calls for super administrators
 */
import API_CONFIG from '../../config';
import axios from 'axios';
import { getSuperAdminAuthHeader } from './superAdminAuthService';

const API_URL = API_CONFIG.BASE_URL;

/**
 * Create a new subscription plan
 * @param {Object} planData - Plan data object
 * @returns {Promise} Promise with created plan data
 */
export const createPlan = async (planData) => {
  try {
    const response = await axios.post(
      `${API_URL}/subscriptions/plans`,
      planData,
      {
        headers: getSuperAdminAuthHeader()
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error creating subscription plan:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to create plan';
      const status = error.response.status;

      switch (status) {
        case 400:
          throw new Error(message || 'Invalid plan data. Please check all fields.');
        case 401:
          throw new Error('Unauthorized. Please login again.');
        case 403:
          throw new Error('Access forbidden. You do not have permission to create plans.');
        case 409:
          throw new Error('Plan code already exists. Please use a different plan code.');
        case 500:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(message || `Failed to create plan (${status})`);
      }
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable. Please try again or contact support.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Update an existing subscription plan
 * @param {string} planId - Plan ID
 * @param {Object} planData - Updated plan data
 * @returns {Promise} Promise with updated plan data
 */
export const updatePlan = async (planId, planData) => {
  try {
    const response = await axios.put(
      `${API_URL}/subscriptions/plans/${planId}`,
      planData,
      {
        headers: getSuperAdminAuthHeader()
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error updating subscription plan:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to update plan';
      const status = error.response.status;

      switch (status) {
        case 400:
          throw new Error(message || 'Invalid plan data.');
        case 401:
          throw new Error('Unauthorized. Please login again.');
        case 403:
          throw new Error('Access forbidden.');
        case 404:
          throw new Error('Plan not found.');
        case 500:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(message || `Failed to update plan (${status})`);
      }
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Get plan by ID
 * @param {string} planId - Plan ID
 * @returns {Promise} Promise with plan data
 */
export const getPlanById = async (planId) => {
  try {
    const response = await axios.get(
      `${API_URL}/subscriptions/plans/${planId}`,
      {
        headers: getSuperAdminAuthHeader()
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching plan:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to fetch plan';
      const status = error.response.status;

      switch (status) {
        case 401:
          throw new Error('Unauthorized. Please login again.');
        case 403:
          throw new Error('Access forbidden.');
        case 404:
          throw new Error('Plan not found.');
        case 500:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(message || `Failed to fetch plan (${status})`);
      }
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Get all subscription plans
 * @returns {Promise} Promise with list of all plans
 */
export const getAllPlans = async () => {
  try {
    const response = await axios.get(
      `${API_URL}/subscriptions/plans/all`,
      {
        headers: getSuperAdminAuthHeader()
      }
    );

    return response.data || [];
  } catch (error) {
    console.error('Error fetching all plans:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to fetch plans';
      const status = error.response.status;

      switch (status) {
        case 401:
          throw new Error('Unauthorized. Please login again.');
        case 403:
          throw new Error('Access forbidden.');
        case 500:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(message || `Failed to fetch plans (${status})`);
      }
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Check if plan code exists
 * @param {string} planCode - Plan code to check
 * @returns {Promise<boolean>} Promise with boolean indicating if code exists
 */
export const checkPlanCodeExists = async (planCode) => {
  try {
    const plans = await getAllPlans();
    return plans.some(plan => plan.planCode === planCode);
  } catch (error) {
    console.error('Error checking plan code:', error);
    return false;
  }
};

// Export default service object
const subscriptionPlanService = {
  createPlan,
  updatePlan,
  getPlanById,
  getAllPlans,
  checkPlanCodeExists
};

export default subscriptionPlanService;

